import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppSettings } from '../lib/app-settings.js';
import {
  bubbleOffset,
  compassDeltaAngle,
  compassStability,
  compassTargetHeading,
  formatCompassHeading,
  formatSignedLevelAngle,
  levelDirection,
  levelQuality,
  levelTargetHint,
  normalizeCompassHeading,
  normalizeLevelAngle,
  rawHeadingAngle,
  rawLevelAngle,
  rollFromGravity,
  shouldNotifyTargetHit,
  squareAlignGuidance,
  targetDeltaAngle,
  zeroLevelAngle,
  type LevelAxis,
  type LevelQuality,
  type CompassStability,
  type OrientationReading,
  type SquareAlignGuidance,
} from '../lib/level.js';
import { lightHaptic } from '../lib/platform.js';
import { SADDLE_CENTER_ANGLE_PRESETS } from './calculator-configs.js';

type SensorStatus = 'idle' | 'requesting' | 'active' | 'denied' | 'unsupported';
type MotionPermissionState = 'granted' | 'denied' | 'prompt';
type LevelToolMode = 'degree-level' | 'square-align';

interface LevelScreenProps {
  toolMode?: LevelToolMode;
}

interface DeviceOrientationEventConstructorWithPermission {
  requestPermission?: () => Promise<MotionPermissionState>;
}

interface DeviceMotionEventConstructorWithPermission {
  requestPermission?: () => Promise<MotionPermissionState>;
}

interface DeviceOrientationEventWithCompass extends DeviceOrientationEvent {
  webkitCompassHeading?: number | null;
}

const EMPTY_READING: OrientationReading = {};
const TARGET_ANGLES = uniqueSortedAngles([0, 2.5, 5, 10, ...SADDLE_CENTER_ANGLE_PRESETS, 90]);
const SQUARE_ALIGN_TARGET_ANGLES = [0, 90, 180, 270];
const MAX_COMPASS_HEADING_SAMPLES = 3;

export default function LevelScreen({ toolMode = 'degree-level' }: LevelScreenProps) {
  const location = useLocation();
  const settings = useAppSettings();
  const levelTolerance = settings.levelTolerance;
  const isSquareAlign = toolMode === 'square-align';
  const requestedTargetAngle = targetAngleForMode(
    targetAngleFromSearch(location.search, toolMode),
    toolMode,
  );
  const returnPath = returnPathFromSearch(location.search);
  // Degree level is locked to the left-right (roll) axis — front-back tilt is ignored.
  const axis: LevelAxis = 'roll';
  const [reading, setReading] = useState<OrientationReading>(EMPTY_READING);
  const [zeroOffsets, setZeroOffsets] = useState<Record<LevelAxis, number>>({
    roll: 0,
    pitch: 0,
  });
  const [sensorStatus, setSensorStatus] = useState<SensorStatus>('idle');
  const [demoMode, setDemoMode] = useState(false);
  const [demoAngle, setDemoAngle] = useState(0);
  const [targetAngle, setTargetAngle] = useState(requestedTargetAngle);
  const [headingReference, setHeadingReference] = useState<number | null>(null);
  const [headingSamples, setHeadingSamples] = useState<number[]>([]);
  const [readingHeld, setReadingHeld] = useState(false);
  const [gravityRoll, setGravityRoll] = useState<number | null>(null);
  const pendingReadingRef = useRef<OrientationReading>(EMPTY_READING);
  const headingSamplesRef = useRef<number[]>([]);
  const readingHeldRef = useRef(false);
  const frameRef = useRef<number | null>(null);
  const motionFrameRef = useRef<number | null>(null);
  const pendingRollRef = useRef<number | null>(null);
  const previousFeedbackAngleRef = useRef<number | null>(null);
  const feedbackReadyRef = useRef(false);

  useEffect(() => {
    setTargetAngle(requestedTargetAngle);
  }, [requestedTargetAngle]);

  useEffect(() => {
    readingHeldRef.current = readingHeld;
  }, [readingHeld]);

  useEffect(() => {
    const orientationEvent = getOrientationEventConstructor();
    if (!orientationEvent) {
      setSensorStatus('unsupported');
      setDemoMode(true);
      return;
    }
    if (typeof orientationEvent.requestPermission !== 'function') {
      setSensorStatus('active');
    }
  }, []);

  useEffect(() => {
    if (sensorStatus !== 'active' || demoMode) return;

    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (readingHeldRef.current) return;
      const compassEvent = event as DeviceOrientationEventWithCompass;
      const nextReading: OrientationReading = {
        alpha: event.alpha,
        beta: event.beta,
        gamma: event.gamma,
      };
      if (compassEvent.webkitCompassHeading !== undefined) {
        nextReading.webkitCompassHeading = compassEvent.webkitCompassHeading;
      }
      if (isSquareAlign) {
        const nextHeadingSamples = nextCompassHeadingSamples(
          headingSamplesRef.current,
          rawHeadingAngle(nextReading),
        );
        if (nextHeadingSamples !== headingSamplesRef.current) {
          headingSamplesRef.current = nextHeadingSamples;
          setHeadingSamples(nextHeadingSamples);
        }
      }
      pendingReadingRef.current = nextReading;
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        setReading(pendingReadingRef.current);
      });
    };

    window.addEventListener('deviceorientation', handleOrientation);
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [demoMode, isSquareAlign, sensorStatus]);

  // The degree level reads left-right tilt from the raw gravity vector instead of
  // DeviceOrientation gamma, so front-back tilt is ignored and it never gimbal-locks
  // when the phone is held near vertical. Square Align stays on the compass heading.
  useEffect(() => {
    if (sensorStatus !== 'active' || demoMode || isSquareAlign) return;

    const handleMotion = (event: DeviceMotionEvent) => {
      if (readingHeldRef.current) return;
      const gravity = event.accelerationIncludingGravity;
      if (!gravity) return;
      const roll = rollFromGravity(gravity.x, gravity.y, gravity.z);
      if (roll === null) return;
      pendingRollRef.current = roll;
      if (motionFrameRef.current !== null) return;
      motionFrameRef.current = window.requestAnimationFrame(() => {
        motionFrameRef.current = null;
        setGravityRoll(pendingRollRef.current);
      });
    };

    window.addEventListener('devicemotion', handleMotion);
    return () => {
      window.removeEventListener('devicemotion', handleMotion);
      if (motionFrameRef.current !== null) {
        window.cancelAnimationFrame(motionFrameRef.current);
        motionFrameRef.current = null;
      }
    };
  }, [demoMode, isSquareAlign, sensorStatus]);

  const sensorRawAngle = gravityRoll ?? rawLevelAngle(reading, axis);
  const headingAngle = rawHeadingAngle(reading);
  const headingStability: CompassStability = isSquareAlign
    ? compassStability(headingSamples)
    : 'unavailable';
  const canSetWallReference = headingAngle !== null && headingStability === 'steady';
  const motionPermissionRequired = shouldRequestMotionPermission(sensorStatus);
  const useDemoReading =
    demoMode ||
    sensorStatus === 'unsupported' ||
    sensorStatus === 'denied' ||
    sensorRawAngle === null;
  const rawAngle = useDemoReading ? demoAngle : sensorRawAngle;
  const levelAngle = zeroLevelAngle(rawAngle, zeroOffsets[axis]);
  const levelQualityState = levelQuality(levelAngle, levelTolerance);
  const showDemoControls = useDemoReading && !isSquareAlign;
  const canHoldReading =
    sensorStatus === 'active' &&
    !demoMode &&
    (isSquareAlign ? headingAngle !== null : sensorRawAngle !== null);
  const returnLabel = returnLabelForPath(returnPath);
  const screenTitle = isSquareAlign ? 'Square Align' : 'Degree level';
  const referenceBadgeText =
    isSquareAlign && headingReference !== null
      ? `Ref ${formatCompassHeading(headingReference)}`
      : isSquareAlign
        ? 'Ref --'
        : `Zero ${formatSignedLevelAngle(zeroOffsets[axis])}`;
  const squareRunAngle =
    isSquareAlign &&
    headingAngle !== null &&
    headingReference !== null &&
    headingStability === 'steady'
      ? normalizeLevelAngle(headingAngle - headingReference)
      : null;
  const feedbackAngle =
    isSquareAlign && squareRunAngle !== null
      ? targetDeltaAngle(squareRunAngle, targetAngle)
      : isSquareAlign
        ? null
        : levelAngle;
  const feedbackTargetAngle = isSquareAlign ? 0 : targetAngle;
  const readoutAngle = isSquareAlign && feedbackAngle !== null ? feedbackAngle : levelAngle;
  const readoutPending = isSquareAlign && feedbackAngle === null;
  const readoutQuality = levelQuality(readoutAngle, levelTolerance);
  const readoutText = readoutPending ? '--' : formatSignedLevelAngle(readoutAngle);
  const readoutLabel = isSquareAlign
    ? squareAlignReadoutLabel(
        feedbackAngle,
        targetAngle,
        headingAngle,
        headingReference,
        headingStability,
        levelTolerance,
      )
    : levelDirection(levelAngle, axis, levelTolerance);
  const targetPanelAngle = isSquareAlign && squareRunAngle !== null ? squareRunAngle : levelAngle;
  const targetHintOverride =
    isSquareAlign && squareRunAngle === null
      ? squareAlignTargetHint(headingAngle, headingReference, headingStability)
      : undefined;
  const returnShortLabel = returnShortLabelForPath(returnPath);

  useEffect(() => {
    if (!canHoldReading && readingHeld) {
      setReadingHeld(false);
    }
  }, [canHoldReading, readingHeld]);

  useEffect(() => {
    const previousAngle = previousFeedbackAngleRef.current;
    if (feedbackAngle === null) {
      feedbackReadyRef.current = true;
      previousFeedbackAngleRef.current = null;
      return;
    }
    if (
      feedbackReadyRef.current &&
      previousAngle !== null &&
      shouldNotifyTargetHit(feedbackAngle, feedbackTargetAngle, previousAngle, levelTolerance)
    ) {
      void lightHaptic();
    }
    feedbackReadyRef.current = true;
    previousFeedbackAngleRef.current = feedbackAngle;
  }, [feedbackAngle, feedbackTargetAngle, levelTolerance]);

  async function startSensor() {
    if (sensorStatus === 'active') return;
    const orientationEvent = getOrientationEventConstructor();
    if (!orientationEvent) {
      setSensorStatus('unsupported');
      setDemoMode(true);
      return;
    }

    setSensorStatus('requesting');
    try {
      if (typeof orientationEvent.requestPermission === 'function') {
        const permission = await orientationEvent.requestPermission();
        if (permission !== 'granted') {
          setSensorStatus('denied');
          setDemoMode(true);
          return;
        }
      }
      // The degree level needs DeviceMotion (gravity) too. iOS links the two
      // permissions, so this resolves silently once orientation is granted.
      const motionEvent = getMotionEventConstructor();
      if (motionEvent && typeof motionEvent.requestPermission === 'function') {
        try {
          await motionEvent.requestPermission();
        } catch {
          // Best-effort; orientation access is already granted.
        }
      }
      setDemoMode(false);
      setReading(EMPTY_READING);
      setGravityRoll(null);
      setReadingHeld(false);
      clearHeadingSamples();
      setSensorStatus('active');
    } catch {
      setSensorStatus('denied');
      setDemoMode(true);
    }
  }

  function zeroCurrentAngle() {
    void lightHaptic();
    setZeroOffsets((current) => ({
      ...current,
      [axis]: rawAngle,
    }));
  }

  function setWallReference() {
    if (!canSetWallReference || headingAngle === null) return;
    void lightHaptic();
    setHeadingReference(headingAngle);
  }

  function resetZero() {
    void lightHaptic();
    setZeroOffsets((current) => ({
      ...current,
      [axis]: 0,
    }));
    if (isSquareAlign) setHeadingReference(null);
  }

  function clearHeadingSamples() {
    headingSamplesRef.current = [];
    setHeadingSamples([]);
  }

  function changeTargetAngle(nextAngle: number) {
    if (nextAngle === targetAngle) return;
    void lightHaptic();
    setTargetAngle(nextAngle);
  }

  function toggleReadingHold() {
    if (!canHoldReading) return;
    void lightHaptic();
    setReadingHeld((current) => !current);
  }

  function updateDemoAngle(nextAngle: number) {
    setDemoAngle(roundToTenth(clamp(nextAngle, -45, 45)));
  }

  return (
    <div style={screenShellStyle}>
      <div style={topBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <Link to={returnPath} aria-label={returnLabel} style={backButtonStyle}>
            <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 22, height: 22 }}>
              <path
                d="M15 5 8 12l7 7"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.4"
              />
            </svg>
            <span style={backButtonTextStyle}>{returnShortLabel}</span>
          </Link>
          <h1 style={titleStyle}>{screenTitle}</h1>
        </div>
      </div>

      <section aria-label={`${screenTitle} reading`} style={levelPanelStyle}>
        <div style={statusRowStyle}>
          <StatusPill
            status={sensorStatus}
            demoMode={demoMode}
            waiting={sensorStatus === 'active' && sensorRawAngle === null && !demoMode}
            toolMode={toolMode}
            readingHeld={readingHeld}
          />
          <span className="mono" style={zeroBadgeStyle}>
            {referenceBadgeText}
          </span>
        </div>

        <div aria-live="polite" style={readoutWrapStyle}>
          <div
            className="mono"
            style={readoutPending ? waitingReadoutStyle : readoutStyleFor(readoutQuality)}
          >
            {readoutText}
          </div>
          <div style={directionStyle}>{readoutLabel}</div>
        </div>

        {isSquareAlign ? (
          <SquareAlignGraphic
            currentHeading={headingAngle}
            referenceHeading={headingReference}
            targetAngle={targetAngle}
          />
        ) : (
          <LevelGraphic angle={levelAngle} quality={levelQualityState} />
        )}

        {isSquareAlign && (
          <CompassReferencePanel
            currentHeading={headingAngle}
            stability={headingStability}
            canSetReference={canSetWallReference}
            referenceHeading={headingReference}
            targetAngle={targetAngle}
            tolerance={levelTolerance}
          />
        )}

        <TargetAnglePanel
          currentAngle={targetPanelAngle}
          targetAngle={targetAngle}
          tolerance={levelTolerance}
          toolMode={toolMode}
          targetHintOverride={targetHintOverride}
          onChange={changeTargetAngle}
        />
      </section>

      <section
        aria-label={isSquareAlign ? 'Square Align controls' : 'Level controls'}
        style={controlsPanelStyle}
      >
        <div style={actionGridStyle}>
          <button
            type="button"
            onClick={() => void startSensor()}
            disabled={sensorStatus === 'requesting'}
            style={actionButtonStyle('secondary', sensorStatus === 'requesting')}
          >
            {startSensorLabel(sensorStatus, motionPermissionRequired, toolMode)}
          </button>
          <button
            type="button"
            onClick={isSquareAlign ? setWallReference : zeroCurrentAngle}
            disabled={isSquareAlign ? !canSetWallReference : false}
            style={actionButtonStyle('primary', isSquareAlign ? !canSetWallReference : false)}
          >
            {isSquareAlign ? (headingReference === null ? 'Set wall ref' : 'Update ref') : 'Zero'}
          </button>
          <button
            type="button"
            aria-label={readingHeld ? 'Resume live reading' : 'Hold reading'}
            aria-pressed={readingHeld}
            onClick={toggleReadingHold}
            disabled={!canHoldReading}
            style={actionButtonStyle(readingHeld ? 'primary' : 'secondary', !canHoldReading)}
          >
            {readingHeld ? 'Live' : 'Hold'}
          </button>
          <button
            type="button"
            onClick={resetZero}
            disabled={isSquareAlign ? headingReference === null : false}
            style={actionButtonStyle(
              'secondary',
              isSquareAlign ? headingReference === null : false,
            )}
          >
            {isSquareAlign ? 'Clear ref' : 'Reset'}
          </button>
        </div>

        <div role="status" aria-live="polite" style={sensorHelpStyle}>
          {sensorHelpText(
            sensorStatus,
            demoMode,
            motionPermissionRequired,
            useDemoReading,
            toolMode,
            readingHeld,
          )}
        </div>

        {showDemoControls && (
          <div style={demoPanelStyle}>
            <div style={demoHeaderStyle}>
              <span>Demo tilt</span>
              <span className="mono">{formatSignedLevelAngle(demoAngle)}</span>
            </div>
            <input
              aria-label="Demo tilt"
              type="range"
              min={-45}
              max={45}
              step={0.1}
              value={demoAngle}
              onChange={(event) => updateDemoAngle(Number(event.target.value))}
              style={demoSliderStyle}
            />
            <div style={demoNudgeGridStyle}>
              <button
                type="button"
                onClick={() => updateDemoAngle(demoAngle - 1)}
                style={demoNudgeStyle}
              >
                -1
              </button>
              <button type="button" onClick={() => updateDemoAngle(0)} style={demoNudgeStyle}>
                0
              </button>
              <button
                type="button"
                onClick={() => updateDemoAngle(demoAngle + 1)}
                style={demoNudgeStyle}
              >
                +1
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function CompassReferencePanel({
  currentHeading,
  stability,
  canSetReference,
  referenceHeading,
  targetAngle,
  tolerance,
}: {
  currentHeading: number | null;
  stability: CompassStability;
  canSetReference: boolean;
  referenceHeading: number | null;
  targetAngle: number;
  tolerance: number;
}) {
  const targetHeading =
    referenceHeading === null ? null : compassTargetHeading(referenceHeading, targetAngle);
  const guidance = squareAlignGuidance(
    currentHeading,
    referenceHeading,
    targetAngle,
    stability,
    tolerance,
  );

  return (
    <div aria-label="Compass reference" style={compassPanelStyle}>
      <div style={compassPanelHeaderStyle}>
        <span>Compass reference</span>
        <span style={compassPanelStatusStyle(stability)}>{compassStatusText(stability)}</span>
      </div>
      <div style={compassMetricGridStyle}>
        <span style={compassMetricStyle}>Heading {formatHeadingValue(currentHeading)}</span>
        <span style={compassMetricStyle}>
          Wall reference {formatReferenceValue(referenceHeading)}
        </span>
        <span style={compassMetricStyle}>
          Target heading {formatTargetHeadingValue(targetHeading)}
        </span>
      </div>
      <span style={compassActionHintStyle}>
        {wallReferenceHint(canSetReference, referenceHeading, stability)}
      </span>
      <SquareAlignGuidanceCard
        guidance={guidance}
        stability={stability}
        targetAngle={targetAngle}
        tolerance={tolerance}
      />
    </div>
  );
}

function wallReferenceHint(
  canSetReference: boolean,
  referenceHeading: number | null,
  stability: CompassStability,
): string {
  if (referenceHeading !== null) {
    if (stability === 'unstable') return 'Reference saved. Move away from steel before aligning.';
    if (stability === 'calibrating') return 'Reference saved. Hold steady before aligning.';
    if (stability === 'unavailable') return 'Reference saved. Restart compass to align.';
    return 'Reference saved. Align pipe to the target heading.';
  }

  return canSetReference
    ? 'Use Set wall ref to save the building line.'
    : 'Use Set wall ref when compass is steady.';
}

function SquareAlignGuidanceCard({
  guidance,
  stability,
  targetAngle,
  tolerance,
}: {
  guidance: SquareAlignGuidance;
  stability: CompassStability;
  targetAngle: number;
  tolerance: number;
}) {
  return (
    <div role="status" aria-live="polite" style={squareGuidanceStyle(guidance.status)}>
      <div style={squareGuidanceMetaStyle}>
        <span style={squareGuidanceTargetStyle}>{squareAlignRunLabel(targetAngle)}</span>
        <span style={squareGuidanceConfidenceStyle}>
          <span style={squareGuidanceMetaLabelStyle}>Confidence</span>
          <span>{compassConfidenceText(stability)}</span>
        </span>
        <span style={squareGuidanceToleranceStyle}>Tolerance {formatTargetAngle(tolerance)}</span>
      </div>
      <span style={squareGuidanceLabelStyle}>{guidance.label}</span>
      <span style={squareGuidanceDetailStyle}>{guidance.detail}</span>
      {typeof guidance.delta === 'number' && (
        <span className="mono" style={squareGuidanceDeltaStyle}>
          {formatSignedLevelAngle(guidance.delta)}
        </span>
      )}
    </div>
  );
}

function TargetAnglePanel({
  currentAngle,
  targetAngle,
  tolerance,
  toolMode,
  targetHintOverride,
  onChange,
}: {
  currentAngle: number;
  targetAngle: number;
  tolerance: number;
  toolMode: LevelToolMode;
  targetHintOverride?: string | undefined;
  onChange: (angle: number) => void;
}) {
  const delta = targetDeltaAngle(currentAngle, targetAngle);
  const quality = levelQuality(delta, tolerance);
  const hintQuality = targetHintOverride ? 'near' : quality;
  const targetAngles = targetAnglesFor(targetAngle, toolMode);
  return (
    <div style={targetPanelStyle}>
      <div style={targetHeaderStyle}>
        <span>Target {formatTargetLabel(targetAngle, toolMode)}</span>
        <span className="mono" style={{ ...targetHintStyle, color: qualityColor(hintQuality) }}>
          {targetHintOverride ?? levelTargetHint(currentAngle, targetAngle, tolerance)}
        </span>
      </div>
      <div style={targetGridStyleFor(toolMode)}>
        {targetAngles.map((angle) => {
          const active = isSameAngle(angle, targetAngle);
          const targetLabel = formatTargetLabel(angle, toolMode);
          return (
            <button
              key={angle}
              type="button"
              aria-label={`${targetLabel} target`}
              aria-pressed={active}
              onClick={() => onChange(angle)}
              style={targetButtonStyle(active)}
            >
              {targetLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function targetAnglesFor(targetAngle: number, toolMode: LevelToolMode): number[] {
  if (toolMode === 'square-align') return SQUARE_ALIGN_TARGET_ANGLES;
  const presetAngles = [...TARGET_ANGLES];
  if (presetAngles.some((angle) => isSameAngle(angle, targetAngle))) return presetAngles;
  return [...presetAngles, targetAngle].sort((a, b) => a - b);
}

function nextCompassHeadingSamples(
  currentSamples: readonly number[],
  heading: number | null,
): number[] {
  if (heading === null) return currentSamples.length === 0 ? (currentSamples as number[]) : [];
  const lastHeading = currentSamples[currentSamples.length - 1];
  if (typeof lastHeading === 'number' && Math.abs(lastHeading - heading) < 0.01) {
    return currentSamples as number[];
  }
  return [...currentSamples.slice(-(MAX_COMPASS_HEADING_SAMPLES - 1)), heading];
}

function compassStatusText(stability: CompassStability): string {
  switch (stability) {
    case 'steady':
      return 'Compass steady';
    case 'unstable':
      return 'Compass unstable';
    case 'calibrating':
      return 'Compass calibrating';
    default:
      return 'Compass unavailable';
  }
}

function compassConfidenceText(stability: CompassStability): string {
  switch (stability) {
    case 'steady':
      return 'Ready - verify';
    case 'unstable':
      return 'Unsteady';
    case 'calibrating':
      return 'Calibrating';
    default:
      return 'No compass';
  }
}

function squareAlignReadoutLabel(
  feedbackAngle: number | null,
  targetAngle: number,
  currentHeading: number | null,
  referenceHeading: number | null,
  stability: CompassStability,
  tolerance: number,
): string {
  if (feedbackAngle !== null) {
    if (Math.abs(feedbackAngle) <= tolerance) {
      return isSquareAlignTarget(targetAngle) ? 'On square' : 'On parallel';
    }
    return feedbackAngle > 0 ? 'Rotate clockwise' : 'Rotate counterclockwise';
  }
  if (currentHeading === null) return 'Start compass';
  if (referenceHeading === null) return 'Set reference';
  if (stability === 'calibrating') return 'Hold steady';
  if (stability === 'unstable') return 'Compass unstable';
  return 'Start compass';
}

function squareAlignTargetHint(
  currentHeading: number | null,
  referenceHeading: number | null,
  stability: CompassStability,
): string {
  if (currentHeading === null) return 'Start compass';
  if (referenceHeading === null) return 'Set ref';
  if (stability === 'calibrating') return 'Hold steady';
  if (stability === 'unstable') return 'Unsteady';
  return 'Start compass';
}

function squareAlignRunLabel(targetAngle: number): string {
  return isSquareAlignTarget(targetAngle) ? 'Square run' : 'Parallel run';
}

function targetAngleForMode(targetAngle: number, toolMode: LevelToolMode): number {
  if (toolMode !== 'square-align') return targetAngle;
  return nearestSquareAlignTarget(targetAngle);
}

function uniqueSortedAngles(angles: readonly number[]): number[] {
  return [...new Set(angles.map(roundToTenth))].sort((a, b) => a - b);
}

function StatusPill({
  status,
  demoMode,
  waiting,
  toolMode,
  readingHeld,
}: {
  status: SensorStatus;
  demoMode: boolean;
  waiting: boolean;
  toolMode: LevelToolMode;
  readingHeld: boolean;
}) {
  const label = statusLabel(status, demoMode, waiting, toolMode, readingHeld);
  const isLive = status === 'active' && !demoMode && !waiting && !readingHeld;
  return (
    <span style={statusPillStyle}>
      <span aria-hidden="true" style={statusDotStyle(isLive)} />
      {label}
    </span>
  );
}

function SquareAlignGraphic({
  currentHeading,
  referenceHeading,
  targetAngle,
}: {
  currentHeading: number | null;
  referenceHeading: number | null;
  targetAngle: number;
}) {
  const squareTarget = isSquareAlignTarget(targetAngle);
  const targetRotation = -targetAngle;
  const delta =
    currentHeading === null || referenceHeading === null
      ? 0
      : compassDeltaAngle(currentHeading, referenceHeading, targetAngle);
  const pipeRotation = targetRotation - clamp(delta, -38, 38);
  const targetLabel = formatTargetAngle(targetAngle);

  return (
    <svg
      viewBox="0 0 340 178"
      role="img"
      aria-label="Square Align layout indicator"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      <rect x="12" y="18" width="316" height="138" rx="18" fill="var(--color-diagram-bg)" />
      <g opacity="0.2" stroke="var(--color-diagram-ghost)" strokeWidth="1">
        {Array.from({ length: 7 }).map((_, index) => (
          <line
            key={`square-grid-x-${index}`}
            x1={54 + index * 38}
            y1="32"
            x2={54 + index * 38}
            y2="142"
          />
        ))}
        {Array.from({ length: 5 }).map((_, index) => (
          <line
            key={`square-grid-y-${index}`}
            x1="36"
            y1={48 + index * 22}
            x2="304"
            y2={48 + index * 22}
          />
        ))}
      </g>
      <line
        x1="64"
        y1="38"
        x2="64"
        y2="138"
        stroke="var(--color-diagram-ghost)"
        strokeWidth="9"
        strokeLinecap="round"
      />
      <line
        x1="64"
        y1="38"
        x2="64"
        y2="138"
        stroke="var(--color-border)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <text x="28" y="32" fill="var(--color-text-muted)" fontSize="10" fontWeight="850">
        Wall ref
      </text>
      <g opacity="0.68" transform={`rotate(${targetRotation} 184 88)`}>
        <line
          x1="96"
          y1="88"
          x2="286"
          y2="88"
          stroke="var(--color-blue)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="7 7"
        />
      </g>
      <g transform={`rotate(${pipeRotation} 184 88)`}>
        <line
          x1="94"
          y1="88"
          x2="290"
          y2="88"
          stroke="rgba(2, 6, 23, 0.45)"
          strokeWidth="21"
          strokeLinecap="round"
          transform="translate(0 6)"
        />
        <line
          x1="94"
          y1="88"
          x2="290"
          y2="88"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="17"
          strokeLinecap="round"
        />
        <line
          x1="94"
          y1="88"
          x2="290"
          y2="88"
          stroke="var(--color-accent)"
          strokeWidth="5"
          strokeLinecap="round"
        />
      </g>
      <circle cx="184" cy="88" r="8" fill="var(--color-blue)" opacity="0.2" />
      <circle cx="184" cy="88" r="4" fill="var(--color-blue)" />
      <path
        d={squareTarget ? 'M184 116 A28 28 0 0 1 156 88' : 'M156 88 A28 28 0 0 1 184 60'}
        fill="none"
        stroke="var(--color-blue)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <text x="218" y="132" fill="var(--color-text-primary)" fontSize="11" fontWeight="900">
        Pipe run
      </text>
      <text x="276" y="44" fill="var(--color-blue)" fontSize="14" fontWeight="900">
        {targetLabel}
      </text>
    </svg>
  );
}

function LevelGraphic({ angle, quality }: { angle: number; quality: LevelQuality }) {
  const visualAngle = clamp(angle, -45, 45);
  const offset = bubbleOffset(angle);
  const bubbleX = 170 + offset * 104;
  const accent = qualityColor(quality);

  return (
    <svg
      viewBox="0 0 340 178"
      role="img"
      aria-label="Visual level indicator"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      <rect x="12" y="18" width="316" height="138" rx="18" fill="var(--color-diagram-bg)" />
      <g opacity="0.18" stroke="var(--color-diagram-ghost)" strokeWidth="1">
        {Array.from({ length: 8 }).map((_, index) => (
          <line
            key={`grid-x-${index}`}
            x1={38 + index * 38}
            y1="34"
            x2={38 + index * 38}
            y2="140"
          />
        ))}
        {Array.from({ length: 5 }).map((_, index) => (
          <line
            key={`grid-y-${index}`}
            x1="30"
            y1={46 + index * 22}
            x2="310"
            y2={46 + index * 22}
          />
        ))}
      </g>
      <line
        x1="42"
        y1="88"
        x2="298"
        y2="88"
        stroke="var(--color-diagram-ghost)"
        strokeWidth="2"
        strokeDasharray="5 8"
        opacity="0.72"
      />
      <g transform={`rotate(${visualAngle} 170 88)`}>
        <line
          x1="64"
          y1="88"
          x2="276"
          y2="88"
          stroke="rgba(2, 6, 23, 0.45)"
          strokeWidth="24"
          strokeLinecap="round"
          transform="translate(0 7)"
        />
        <line
          x1="64"
          y1="88"
          x2="276"
          y2="88"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="18"
          strokeLinecap="round"
        />
        <line
          x1="64"
          y1="88"
          x2="276"
          y2="88"
          stroke={accent}
          strokeWidth="6"
          strokeLinecap="round"
          opacity="0.96"
        />
      </g>
      <path
        d="M74 136 A116 116 0 0 1 266 136"
        fill="none"
        stroke="var(--color-border)"
        strokeWidth="2"
      />
      {[-30, -15, 0, 15, 30].map((tick) => {
        const radians = ((tick - 90) * Math.PI) / 180;
        const x1 = 170 + Math.cos(radians) * 104;
        const y1 = 136 + Math.sin(radians) * 104;
        const x2 = 170 + Math.cos(radians) * 114;
        const y2 = 136 + Math.sin(radians) * 114;
        return (
          <line
            key={tick}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={tick === 0 ? accent : 'var(--color-diagram-ghost)'}
            strokeWidth={tick === 0 ? 3 : 2}
            strokeLinecap="round"
          />
        );
      })}
      <circle cx={bubbleX} cy="88" r="16" fill={accent} opacity="0.24" />
      <circle cx={bubbleX} cy="88" r="9" fill={accent} />
      <line x1="170" y1="52" x2="170" y2="124" stroke={accent} strokeWidth="2" opacity="0.48" />
    </svg>
  );
}

function getOrientationEventConstructor(): DeviceOrientationEventConstructorWithPermission | null {
  if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window)) return null;
  return window.DeviceOrientationEvent as unknown as DeviceOrientationEventConstructorWithPermission;
}

function getMotionEventConstructor(): DeviceMotionEventConstructorWithPermission | null {
  if (typeof window === 'undefined' || !('DeviceMotionEvent' in window)) return null;
  return window.DeviceMotionEvent as unknown as DeviceMotionEventConstructorWithPermission;
}

function shouldRequestMotionPermission(status: SensorStatus): boolean {
  if (status === 'active' || status === 'unsupported') return false;
  return typeof getOrientationEventConstructor()?.requestPermission === 'function';
}

function startSensorLabel(
  status: SensorStatus,
  motionPermissionRequired: boolean,
  toolMode: LevelToolMode,
): string {
  if (status === 'requesting') return 'Starting';
  if (status === 'active') return toolMode === 'square-align' ? 'Compass on' : 'Sensor';
  if (motionPermissionRequired) return 'Allow Motion';
  return toolMode === 'square-align' ? 'Start compass' : 'Start';
}

function sensorHelpText(
  status: SensorStatus,
  demoMode: boolean,
  motionPermissionRequired: boolean,
  useDemoReading: boolean,
  toolMode: LevelToolMode,
  readingHeld: boolean,
): string {
  if (toolMode === 'square-align') {
    if (readingHeld) return 'Compass reading held. Tap Live to resume.';
    if (status === 'denied') return 'Compass access blocked. Square Align needs live compass data.';
    if (status === 'unsupported')
      return 'No compass sensor found. Square Align needs live compass data.';
    if (status === 'requesting') return 'Waiting for motion and compass permission.';
    if (motionPermissionRequired) return 'Allow motion access to use Square Align.';
    if (status === 'active' && useDemoReading && !demoMode)
      return 'Move the phone away from steel to wake the compass.';
    if (status === 'active') return 'Live compass reading.';
    return 'Tap Start for live compass mode.';
  }
  if (readingHeld) return 'Reading held. Tap Live to resume.';
  if (status === 'denied') return 'Motion access blocked. You can still use demo tilt.';
  if (status === 'unsupported') return 'No motion sensor found. Demo tilt is available.';
  if (status === 'requesting') return 'Waiting for motion permission.';
  if (motionPermissionRequired) return 'Allow motion access to use the live degree level.';
  if (status === 'active' && useDemoReading && !demoMode)
    return 'Move the phone slightly to wake the sensor.';
  if (status === 'active') return 'Live sensor reading.';
  return 'Tap Start for live sensor mode.';
}

function statusLabel(
  status: SensorStatus,
  demoMode: boolean,
  waiting: boolean,
  toolMode: LevelToolMode,
  readingHeld: boolean,
): string {
  if (readingHeld) return 'Held';
  if (demoMode) {
    if (toolMode === 'square-align') {
      if (status === 'denied') return 'Compass blocked';
      if (status === 'unsupported') return 'No compass';
      return 'Compass off';
    }
    return 'Demo mode';
  }
  if (waiting) return 'Waiting';
  switch (status) {
    case 'active':
      return toolMode === 'square-align' ? 'Compass on' : 'Sensor on';
    case 'requesting':
      return 'Starting';
    case 'denied':
      return toolMode === 'square-align' ? 'Compass blocked' : 'Sensor blocked';
    case 'unsupported':
      return toolMode === 'square-align' ? 'No compass' : 'No sensor';
    default:
      return 'Tap Start';
  }
}

function qualityColor(quality: LevelQuality): string {
  if (quality === 'level') return 'var(--color-success)';
  if (quality === 'near') return 'var(--color-blue)';
  return 'var(--color-accent)';
}

function readoutStyleFor(quality: LevelQuality): CSSProperties {
  return {
    color: qualityColor(quality),
    fontSize: 'clamp(4.6rem, 22vw, 6.75rem)',
    fontWeight: 850,
    lineHeight: 0.92,
    letterSpacing: 0,
    textAlign: 'center',
  };
}

const waitingReadoutStyle: CSSProperties = {
  ...readoutStyleFor('near'),
  color: 'var(--color-text-muted)',
};

function actionButtonStyle(kind: 'primary' | 'secondary', disabled = false): CSSProperties {
  const isPrimary = kind === 'primary';
  return {
    minHeight: 52,
    borderRadius: 'var(--radius-md)',
    color: isPrimary ? 'var(--color-text-on-orange)' : 'var(--color-text-primary)',
    background: isPrimary ? 'var(--color-accent)' : 'var(--color-bg)',
    border: `1px solid ${isPrimary ? 'var(--color-accent)' : 'var(--color-border)'}`,
    fontWeight: 900,
    opacity: disabled ? 0.55 : 1,
  };
}

function statusDotStyle(active: boolean): CSSProperties {
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: active ? 'var(--color-success)' : 'var(--color-accent)',
    boxShadow: active ? '0 0 0 4px rgba(34, 197, 94, 0.14)' : 'none',
  };
}

function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function targetAngleFromSearch(search: string, toolMode: LevelToolMode): number {
  const raw = new URLSearchParams(search).get('target');
  if (raw === null && toolMode === 'square-align') return 90;
  const parsed = raw === null ? 0 : Number(raw);
  if (!Number.isFinite(parsed)) return 0;
  if (toolMode === 'square-align') return nearestSquareAlignTarget(parsed);
  return roundToTenth(clamp(parsed, 0, 90));
}

function returnPathFromSearch(search: string): string {
  const value = new URLSearchParams(search).get('returnTo');
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('://') ||
    value.startsWith('/level') ||
    value.startsWith('/square-align')
  ) {
    return '/';
  }
  return value;
}

function returnLabelForPath(returnPath: string): string {
  const pathOnly = returnPath.split(/[?#]/)[0];
  if (pathOnly === '/') return 'Back to all bends';
  if (pathOnly === '/settings') return 'Back to settings';
  return 'Back to calculator';
}

function returnShortLabelForPath(returnPath: string): string {
  const pathOnly = returnPath.split(/[?#]/)[0];
  if (pathOnly === '/') return 'Bends';
  if (pathOnly === '/settings') return 'Settings';
  return 'Calc';
}

function formatTargetAngle(angle: number): string {
  return `${Number.isInteger(angle) ? angle.toFixed(0) : angle.toFixed(1)}\u00b0`;
}

function formatTargetLabel(angle: number, toolMode: LevelToolMode): string {
  if (toolMode === 'square-align') {
    return `${isSquareAlignTarget(angle) ? 'Square' : 'Parallel'} ${formatTargetAngle(angle)}`;
  }
  return formatTargetAngle(angle);
}

function formatHeadingValue(heading: number | null): string {
  return heading === null ? 'Unavailable' : formatCompassHeading(heading);
}

function formatReferenceValue(heading: number | null): string {
  return heading === null ? 'Not set' : formatCompassHeading(heading);
}

function formatTargetHeadingValue(heading: number | null): string {
  return heading === null ? 'Set ref' : formatCompassHeading(heading);
}

function isSameAngle(firstAngle: number, secondAngle: number): boolean {
  return Math.abs(firstAngle - secondAngle) < 0.01;
}

function isSquareAlignTarget(angle: number): boolean {
  const normalized = normalizeCompassHeading(angle);
  return isSameAngle(normalized, 90) || isSameAngle(normalized, 270);
}

function nearestSquareAlignTarget(angle: number): number {
  const normalized = normalizeCompassHeading(angle);
  return SQUARE_ALIGN_TARGET_ANGLES.reduce((nearest, candidate) => {
    const nearestDelta = Math.abs(normalizeLevelAngle(nearest - normalized));
    const candidateDelta = Math.abs(normalizeLevelAngle(candidate - normalized));
    return candidateDelta < nearestDelta ? candidate : nearest;
  }, SQUARE_ALIGN_TARGET_ANGLES[0] ?? 0);
}

const screenShellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  maxWidth: 430,
  width: '100%',
  margin: '0 auto',
};

const topBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  minHeight: 44,
  marginBottom: 'var(--space-1)',
};

const backButtonStyle: CSSProperties = {
  height: 34,
  minWidth: 34,
  minHeight: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  color: 'var(--color-text-primary)',
  borderRadius: 'var(--radius-pill)',
  padding: '0 var(--space-2) 0 var(--space-1)',
  marginLeft: '-8px',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const backButtonTextStyle: CSSProperties = {
  maxWidth: 62,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--text-lg)',
  fontWeight: 850,
  lineHeight: 1.1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const levelPanelStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  padding: 'var(--space-4)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
};

const statusRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
};

const statusPillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: 30,
  padding: 'var(--space-1) var(--space-3)',
  borderRadius: 'var(--radius-pill)',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  fontSize: 'var(--text-xs)',
  fontWeight: 850,
};

const zeroBadgeStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
  fontWeight: 850,
  whiteSpace: 'nowrap',
};

const readoutWrapStyle: CSSProperties = {
  display: 'grid',
  justifyItems: 'center',
  gap: 'var(--space-2)',
  paddingTop: 'var(--space-1)',
};

const directionStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const targetPanelStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-3)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
};

const compassPanelStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-3)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
};

const compassPanelHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
};

function compassPanelStatusStyle(stability: CompassStability): CSSProperties {
  const palette =
    stability === 'steady'
      ? {
          color: 'var(--color-success)',
          background: 'rgba(34, 197, 94, 0.12)',
          border: '1px solid rgba(34, 197, 94, 0.24)',
        }
      : stability === 'unstable'
        ? {
            color: 'var(--color-accent)',
            background: 'rgba(255, 106, 26, 0.12)',
            border: '1px solid rgba(255, 106, 26, 0.28)',
          }
        : {
            color: 'var(--color-blue)',
            background: 'var(--color-blue-soft)',
            border: '1px solid rgba(0, 122, 255, 0.18)',
          };
  return {
    ...palette,
    borderRadius: 'var(--radius-pill)',
    padding: '2px var(--space-2)',
    fontSize: '0.64rem',
    textTransform: 'uppercase',
  };
}

const compassMetricGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 'var(--space-1)',
};

const compassMetricStyle: CSSProperties = {
  minHeight: 38,
  display: 'grid',
  alignItems: 'center',
  padding: 'var(--space-2)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  lineHeight: 1.2,
};

const compassActionHintStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
  fontWeight: 750,
  lineHeight: 1.25,
};

function squareGuidanceStyle(status: SquareAlignGuidance['status']): CSSProperties {
  const aligned = status === 'aligned';
  const warning = status === 'unstable' || status === 'unavailable';
  return {
    display: 'grid',
    gridTemplateColumns:
      status === 'turn-clockwise' || status === 'turn-counterclockwise'
        ? 'minmax(0, 1fr) auto'
        : 'minmax(0, 1fr)',
    gap: 'var(--space-1)',
    alignItems: 'center',
    color: aligned ? 'var(--color-success)' : warning ? 'var(--color-accent)' : 'var(--color-blue)',
    background: aligned
      ? 'rgba(34, 197, 94, 0.12)'
      : warning
        ? 'rgba(255, 106, 26, 0.12)'
        : 'var(--color-blue-soft)',
    border: `1px solid ${
      aligned
        ? 'rgba(34, 197, 94, 0.24)'
        : warning
          ? 'rgba(255, 106, 26, 0.28)'
          : 'rgba(0, 122, 255, 0.18)'
    }`,
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-3)',
  };
}

const squareGuidanceLabelStyle: CSSProperties = {
  fontSize: 'var(--text-xl)',
  fontWeight: 900,
};

const squareGuidanceMetaStyle: CSSProperties = {
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 'var(--space-1)',
  alignItems: 'center',
};

const squareGuidanceTargetStyle: CSSProperties = {
  minHeight: 32,
  display: 'grid',
  alignItems: 'center',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--space-1) var(--space-2)',
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
  lineHeight: 1.15,
};

const squareGuidanceConfidenceStyle: CSSProperties = {
  minHeight: 32,
  display: 'grid',
  alignItems: 'center',
  gap: 1,
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--space-1) var(--space-2)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  lineHeight: 1.05,
};

const squareGuidanceMetaLabelStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '0.64rem',
  textTransform: 'uppercase',
};

const squareGuidanceToleranceStyle: CSSProperties = {
  minHeight: 32,
  display: 'grid',
  alignItems: 'center',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--space-1) var(--space-2)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  lineHeight: 1.15,
};

const squareGuidanceDetailStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-xs)',
  fontWeight: 750,
  lineHeight: 1.25,
};

const squareGuidanceDeltaStyle: CSSProperties = {
  gridColumn: '2',
  gridRow: '1 / span 2',
  fontSize: 'var(--text-lg)',
  fontWeight: 900,
};

const targetHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
};

const targetHintStyle: CSSProperties = {
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
  whiteSpace: 'nowrap',
};

function targetGridStyleFor(toolMode: LevelToolMode): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns:
      toolMode === 'square-align' ? 'repeat(2, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))',
    gap: 'var(--space-1)',
  };
}

function targetButtonStyle(active: boolean): CSSProperties {
  return {
    minWidth: 0,
    minHeight: 38,
    borderRadius: 'var(--radius-sm)',
    color: active ? 'var(--color-text-on-orange)' : 'var(--color-text-primary)',
    background: active ? 'var(--color-accent)' : 'var(--color-surface)',
    border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
    fontSize: 'var(--text-sm)',
    fontWeight: 900,
  };
}

const controlsPanelStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  padding: 'var(--space-3)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
};

const actionGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--space-2)',
};

const sensorHelpStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 750,
  lineHeight: 1.25,
};

const demoPanelStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-3)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
};

const demoHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 850,
};

const demoSliderStyle: CSSProperties = {
  width: '100%',
  accentColor: 'var(--color-accent)',
};

const demoNudgeGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 'var(--space-2)',
};

const demoNudgeStyle: CSSProperties = {
  minHeight: 42,
  borderRadius: 'var(--radius-sm)',
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  fontWeight: 900,
};
